import type { GetStartedAction } from "../GetStartedScreen";
import {
  ARTIST_GET_STARTED_ACTIONS,
  COLLECTOR_GET_STARTED_ACTIONS,
  GALLERY_GET_STARTED_ACTIONS,
} from "./GetStartedStep";
import { ARTIST_GOALS } from "./artistGoals";
import { COLLECTOR_GOALS } from "./collectorGoals";
import { GALLERY_GOALS } from "./galleryGoals";
import type { GalleryGoalOption } from "./galleryGoals";
import { JEWELRY_GOALS } from "./jewelryGoals";

// One entry per persona: the copy and behaviour flags GalleryPracticeStep.tsx, GalleryAccountStep.tsx
// and GetStartedStep.tsx read for whichever persona is active. OnboardingV2.tsx imports this table
// and passes the right column to all three screens (Phase 5 onward, Phase 4 of ACA-269 for
// Jewelry). No C# ordinal for User.Type travels with it, but where it comes from varies by
// persona: the artist's is decided server-side by POST /registrations/account, while the
// gallery's and the Collector's travel explicitly in POST /register, from props the container
// passes in (galleryUserTypeValue, collectorUserTypeValue). Jewelry reuses galleryUserTypeValue
// (D2 - a Jewelry account IS a Gallery account, UserType 3) and adds DefaultInventoryType from its
// own prop, jewelryInventoryTypeValue - the one ordinal it does carry of its own.
export interface PersonaCopy {
  goals: readonly GalleryGoalOption[];
  practiceHeading: string;
  practiceSectionLabel?: string;
  practiceBackLabel: string;
  accountBackLabel: string;
  orgLabel: string;
  orgPlaceholder: string;
  siteLabel: string;
  sitePlaceholder: string;
  emailPlaceholder: string;
  practiceFields: boolean;
  careerFields: boolean;
  orgRequired: boolean;
  validateOrgNameFormat: boolean;
  accountOrgFields: boolean;
  /** Runs validateName's format check on the account step's Full Name (GalleryAccountStep.tsx's
      `validateFullNameFormat`). False for the gallery, which never validated Full Name beyond
      non-blank; true for the artist, whose OnboardingProfileScreen already ran validateName. */
  validateFullNameFormat: boolean;
  goalTestIdPrefix: string;
  getStartedActions: readonly GetStartedAction[];
}

// `practiceFields` and `accountOrgFields` are mutually exclusive by persona - they name the same
// section (organisation name, website, and - behind `careerFields` - career stage and annual
// sales), just at different steps. The artist keeps it on step 2, alongside the goals - the
// Collector does too, since 26/09 (D1, below), and so does Jewelry, since 29/09 (ACA-269's own
// D1); the gallery is the outlier, having moved it to step 3 on 24/09 (GalleryAccountStep's
// Gallery Name / Gallery Website fields) and dropped career stage and annual sales from signup
// entirely. Nobody should read both flags as true for one persona - that would mean the section is
// meant to render twice.
//
// `goalTestIdPrefix` exists because the testids collide: goal values are SignupIntent flags, and
// GALLERY_GOALS and ARTIST_GOALS share bits (2, 4 and 128 all appear in both); JEWELRY_GOALS
// shares two more with the gallery's own table (16 and 32 - see jewelryGoals.ts) since ACA-269;
// ARTIST_GOALS and COLLECTOR_GOALS share a fourth bit on top of that (256, Catalogue -
// collectorGoals.ts's own doc comment documents the reuse). The existing e2e suite
// (e2e/tests/auth/register.spec.ts) already targets `gallery-goal-2`, so the default has to stay
// "gallery-goal".

export const GALLERY_PERSONA_COPY: PersonaCopy = {
  goals: GALLERY_GOALS,
  practiceHeading: "Tell us about your business.",
  practiceBackLabel: "Gallery",
  accountBackLabel: "Your organisation",
  orgLabel: "Gallery Name",
  orgPlaceholder: "Gallery or business name",
  siteLabel: "Gallery Website",
  sitePlaceholder: "yourgallery.com",
  emailPlaceholder: "you@gallery.com",
  practiceFields: false,
  careerFields: false,
  orgRequired: true,
  validateOrgNameFormat: true,
  accountOrgFields: true,
  validateFullNameFormat: false,
  goalTestIdPrefix: "gallery-goal",
  getStartedActions: GALLERY_GET_STARTED_ACTIONS,
};

export const ARTIST_PERSONA_COPY: PersonaCopy = {
  goals: ARTIST_GOALS,
  practiceHeading: "Tell us about your practice.",
  practiceSectionLabel: "Your practice",
  practiceBackLabel: "Artist",
  accountBackLabel: "Your practice",
  orgLabel: "Studio Name",
  orgPlaceholder: "Your studio name",
  siteLabel: "Artist Website",
  sitePlaceholder: "yourstudio.com",
  emailPlaceholder: "you@studio.com",
  practiceFields: true,
  careerFields: true,
  orgRequired: true,
  validateOrgNameFormat: true,
  accountOrgFields: false,
  validateFullNameFormat: true,
  goalTestIdPrefix: "artist-goal",
  getStartedActions: ARTIST_GET_STARTED_ACTIONS,
};

// Most values below are what the prototype (App Onboarding.dc.html) renders when the picker hands
// it Collector — including two that only read this way because they fall through the prototype's
// ternary over `artist`, and sound off for someone cataloguing a collection: practiceHeading
// ("Tell us about your business.") and accountBackLabel ("Your organisation"). Rewriting either
// would be designing product, which this table doesn't do (D3). Both are raised as an open
// question for product/design and ship as the prototype wrote them until that's answered.
// The two placeholders and the get-started list below are the exceptions - design answered
// for those on 28/09.
export const COLLECTOR_PERSONA_COPY: PersonaCopy = {
  goals: COLLECTOR_GOALS,
  practiceHeading: "Tell us about your business.", // fall-through of the ternary over `artist` (:439)
  practiceSectionLabel: "Your organisation", // fall-through of the same ternary (:441-442) - same text as accountBackLabel below, now actually rendered (see practiceFields)
  practiceBackLabel: "Collector", // authorial — the prototype's roleLabel
  accountBackLabel: "Your organisation", // fall-through of the same ternary (:441-442)
  orgLabel: "Collection Name", // authorial (orgLabels.collector, :370)
  orgPlaceholder: "Business name", // 28/09, design - was the prototype's "Gallery or business name"
  siteLabel: "Website", // authorial (:469)
  sitePlaceholder: "yourbusiness.com", // 28/09, design - was the prototype's "yourgallery.com"
  emailPlaceholder: "you@email.com", // authorial (:465)
  // D1, decided 26/09 after QA held the shipped screen up against the signed prototype: the
  // prototype's step 2 draws a "YOUR ORGANISATION" section under the goal chips - Collection Name,
  // Website, Career stage (required) and Annual sales - and step 3 has none of the four. That is
  // the SAME step the artist already uses, not a fourth arrangement of its own: practiceFields
  // true, accountOrgFields false. What lived here before ("both false" - this persona asks for
  // organisation info at no step at all) followed the Collector's legacy RegistrationForm instead
  // of the prototype, which is the plan's own recommendation (§4 D1) - overturned by the user once
  // the comparison above was actually run. The gallery is the one persona that moved this section
  // to step 3 (24/09 product decision, GalleryAccountStep's own Gallery Name / Gallery Website
  // fields) and dropped career stage/annual sales from signup entirely; that decision was about the
  // gallery's own screen, not a rule against the section appearing anywhere else.
  practiceFields: true,
  careerFields: false, // 28/09, design
  orgRequired: false, // design - the prototype marks no field here required
  validateOrgNameFormat: false, // 29/09, review - "Smith & Jones Collection"
  accountOrgFields: false,
  // orgLabel, siteLabel and practiceSectionLabel above are authorial/fall-through prototype copy,
  // and they render now: practiceFields being true is what puts GalleryPracticeStep's organisation
  // section on screen, the same mechanism the artist's copy already renders through.
  // accountOrgFields stays false, so GalleryAccountStep still shows neither field on step 3 - the
  // one piece of the prior read (D1: "step 3 has none of them") that is still correct.
  validateFullNameFormat: false, // the Collector's legacy form never validated Full Name beyond non-blank
  goalTestIdPrefix: "collector-goal",
  getStartedActions: COLLECTOR_GET_STARTED_ACTIONS, // D5, closed 28/09 - the consign row comes out
};

// Most values below are what the prototype (App Onboarding.dc.html) renders when the picker hands
// it Jewelry - including two that only read this way because they fall through the prototype's
// ternary over `artist`, the same fall-through the Collector's table documents above:
// practiceHeading ("Tell us about your business.") and accountBackLabel ("Your organisation").
// The design's own word for this persona is "Business", not the legacy's "Boutique" (D3) - the
// legacy coerces a Jewelry account's Type.Name to "Gallery" and swaps the display word in
// getTypeDisplay(); the design just writes "Business" directly, so there's no coercion to mirror.
export const JEWELRY_PERSONA_COPY: PersonaCopy = {
  goals: JEWELRY_GOALS,
  practiceHeading: "Tell us about your business.", // fall-through of the ternary over `artist` (:439)
  practiceSectionLabel: "Your organisation", // fall-through of the same ternary (:441-442), and renders (practiceFields true)
  practiceBackLabel: "Jewelry", // authorial - the prototype's roleLabel
  accountBackLabel: "Your organisation", // fall-through of the same ternary, confirmed at the step 3 chevron
  orgLabel: "Business Name", // design (orgLabels.jewelry, :370)
  orgPlaceholder: "Business name", // decided by the user 29/09 (plan §7.3) - the prototype's own
  // fall-through here is "Gallery or business name", which renders next to a "Business Name" label
  // (the same defect design already fixed for the Collector's placeholder on 28/09; see this
  // file's COLLECTOR_PERSONA_COPY entry and personaCopy.test.ts's `.not.toMatch(/gallery/i)` guard)
  siteLabel: "Business Website", // design, :469
  sitePlaceholder: "yourbusiness.com", // design, :470
  emailPlaceholder: "you@business.com", // design, :465
  practiceFields: true, // D1 - organisation section on step 2, the artist's and Collector's arrangement, not the gallery's
  careerFields: true, // §7.2(a), 29/09 - the prototype marks Career stage required for Jewelry
  orgRequired: false, // the prototype marks no asterisk on Business Name
  // F8 review (29/09): the entry this replaces read the gallery's own `true` literally, on the
  // premise that a Jewelry account IS a Gallery account and should therefore inherit its format
  // check. Both halves of that premise turn out false on inspection. First, the gallery's `true`
  // is never actually read for the gallery: GalleryPracticeStep is the only reader of this flag,
  // gated behind `practiceFields`, which is false for the gallery (see above) - the gallery's real
  // organisation field lives on GalleryAccountStep instead, and that component runs no format
  // check on it at all (its own doc comment: "the gallery has never had a format check"). Second,
  // the legacy Jewelry form has no format check either: page/Onboarding.jsx's BusinessName input
  // (the `!isCollector` branch, shared with the gallery's own legacy field) is a plain `type="text"`
  // with no `required` and no validation - handleContinue there checks Email/Password/Terms and,
  // for an artist, Career stage/Annual sales, never BusinessName.
  //
  // So neither the gallery's flag nor the legacy Jewelry form actually enforces a character rule -
  // `true` here would have made Jewelry the only persona whose organisation name is restricted,
  // with no legacy behaviour or gallery precedent to justify it. "Tiffany & Co." is the case this
  // was caught on: with `true` it never reaches step 3, on the same "Letters, numbers, spaces,
  // hyphens and apostrophes only" message ACA-268 already fixed for the Collector's "Smith & Jones
  // Collection" (commit 7abf64dfe9) - see COLLECTOR_PERSONA_COPY's own `validateOrgNameFormat:
  // false` above for that precedent. The artist's `true` is NOT the same mistake repeated:
  // OnboardingProfileScreen, the artist's own legacy screen, already runs validateName on Studio
  // Name - not in `handleContinue` (that is just the submit handler `form.onSubmit` wraps, so it
  // fires downstream of the check, not the place it lives), but in its form's own
  // `validate.studioName` and the `canSubmit` gate. So the artist's flag is faithful to a real
  // legacy check - Jewelry has no such legacy check to be faithful to.
  validateOrgNameFormat: false,
  accountOrgFields: false, // the prototype's step 3 has no organisation fields
  validateFullNameFormat: false, // the legacy registration form never validated Full Name beyond non-blank
  goalTestIdPrefix: "jewelry-goal",
  // D4 - TASKS has no `jewelry` key in the prototype, so it falls through to TASKS.gallery, consign
  // row included; the legacy flow does the same (Jewelry is a Gallery account). This is NOT the
  // Collector's filtered list (COLLECTOR_GET_STARTED_ACTIONS drops "consign") - that filter was a
  // 28/09 design decision specific to the Collector, not a general rule to reapply here.
  getStartedActions: GALLERY_GET_STARTED_ACTIONS,
};
